import type { DrawData } from '../../../../../schema/draw/entry.js';
import type { BettingEvent } from '../../../../../schema/sport/entry.js';
import type { SportEventOdds, SportEventSchema } from '../../../../../schema/sportevent/entry.js';

/**
 * Maps a betting event from draw data to a sport event format.
 *
 * @param bettingEvent - The source betting event containing event details and participant references
 * @param drawData - The draw data containing leagues, countries, and participants
 * @returns The mapped sport event data or undefined if required data is missing
 */
export function mapDrawEventToSportEvent(bettingEvent: BettingEvent, drawData: DrawData): SportEventSchema | undefined {
  const league = drawData.leagues[bettingEvent.leagueId];

  if (!league) {
    return undefined;
  }

  const leagueCountry = drawData.countries[league.countryId];

  if (!leagueCountry) {
    return undefined;
  }

  const eventParticipants: SportEventSchema['participants'] = [];
  for (const participantId of bettingEvent.participants) {
    const participant = drawData.participants[participantId];

    if (!participant) {
      continue;
    }

    const participantCountry = drawData.countries[participant.countryId];

    if (!participantCountry) {
      continue;
    }

    eventParticipants.push({ ...participant, country: participantCountry });
  }

  return {
    startTime: bettingEvent.startDate,
    providerIds: bettingEvent.externalId,
    league: {
      country: leagueCountry,
      leagueId: bettingEvent.leagueId,
      name: league.name,
    },
    participants: eventParticipants,
    // Data below will be patched when event data is prefetched to client
    arena: undefined,
    seasonInfo: '',
    status: bettingEvent.status,
  } satisfies SportEventSchema;
}

/**
 * Maps betting event odds from draw data to sport event odds format.
 *
 * @param bettingEvent - The betting event containing event type statistics reference
 * @param drawData - The draw data containing event type statistics
 * @returns The mapped sport event odds or undefined if statistics are not found
 */
export function mapDrawEventOddsToSportEventOdds(
  bettingEvent: BettingEvent,
  drawData: DrawData
): SportEventOdds | undefined {
  const stats = drawData.eventTypeStatistics[bettingEvent.eventTypeStatisticsId];

  if (!stats) {
    return undefined;
  }

  return {
    [stats.eventType]: stats,
  } satisfies SportEventOdds;
}
